import { useState } from 'react'
import { Target } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSetTarget, type TeamPerson } from './api'

/** "What are you aiming for today?" in one line, plus an optional number of hours. */
export function TargetEditor({ current }: { current: TeamPerson['target'] }) {
  const save = useSetTarget()
  const [text, setText] = useState(current?.text ?? '')
  const [hours, setHours] = useState(current?.minutes ? String(current.minutes / 60) : '')
  const minutes = hours.trim() === '' ? null : Math.round(Number(hours) * 60)
  const invalid = minutes !== null && (!Number.isFinite(minutes) || minutes < 0 || minutes > 960)
  const dirty = (text.trim() || null) !== (current?.text ?? null) || (minutes || null) !== (current?.minutes ?? null)

  function submit() {
    if (invalid || save.isPending) return
    save.mutate({ text: text.trim() || null, minutes: minutes || null })
  }

  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_7rem_auto] sm:items-end">
      <label className="grid gap-1 text-xs font-medium text-text-secondary">
        Your target for today
        <span className="relative">
          <Target className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
          <Input
            value={text}
            maxLength={200}
            placeholder="e.g. Finish the invoice export and review two PRs"
            className="pl-8"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </span>
      </label>
      <label className="grid gap-1 text-xs font-medium text-text-secondary">
        Hours of work
        <Input
          value={hours}
          inputMode="decimal"
          placeholder="e.g. 4"
          aria-invalid={invalid}
          onChange={(e) => setHours(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
      </label>
      <Button type="button" onClick={submit} disabled={!dirty || invalid || save.isPending}>
        {save.isPending ? 'Saving…' : current ? 'Update' : 'Set target'}
      </Button>
    </div>
  )
}
